import { DEFAULT_OG_IMAGE, getPosterUrl, getShowField } from "./showUtils.js";
import { cleanViewerFacingCopy } from "../../utils/viewerFacingCopy.js";
import { getDisplayNetwork } from "../../utils/networkNames.js";

const SITE_URL = "https://premierelens.com";
const MAX_DESCRIPTION_LENGTH = 155;

// Map ISO 639-2/3 language codes to og:locale format
const LANGUAGE_TO_LOCALE = {
  eng: "en_US",
  spa: "es_ES",
  kor: "ko_KR",
  deu: "de_DE",
  fra: "fr_FR",
  ita: "it_IT",
  jpn: "ja_JP",
  por: "pt_BR",
  rus: "ru_RU",
  zho: "zh_CN",
  zhtw: "zh_TW",
  nor: "nb_NO",
  swe: "sv_SE",
  dan: "da_DK",
  fin: "fi_FI",
  nld: "nl_NL",
  pol: "pl_PL",
  tur: "tr_TR",
  hin: "hi_IN",
  ara: "ar_SA",
  tha: "th_TH",
};

function getShowLocale(show) {
  const lang = show?.posterMetadata?.language;
  if (!lang) return "en_US";
  return LANGUAGE_TO_LOCALE[lang] || "en_US";
}

export function formatShortDate(isoDate) {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function compactWhitespace(value = "") {
  return String(value).replace(/\s+/g, " ").trim();
}

function withPeriod(value = "") {
  const trimmed = compactWhitespace(value);
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

const MIN_PLOT_LENGTH = 40;
const MAX_TITLE_LENGTH = 65;

function getSourceLabel(show) {
  return getDisplayNetwork(show);
}

function buildShowTitle(title, year, network) {
  const base = `${title}${year ? ` (${year})` : ""}`;
  if (network) {
    const candidate = `${base} on ${network} | PremiereLens`;
    if (candidate.length <= MAX_TITLE_LENGTH) return candidate;
  }
  return `${base} | PremiereLens`;
}

function buildFactLine(show) {
  const network = getSourceLabel(show);
  const releaseDate = getShowField(show, "releaseDate");
  const seasonNumber = getShowField(show, "seasonNumber");

  let line = "";
  if (releaseDate) line += `Premieres ${formatShortDate(releaseDate)}`;
  if (network) line += `${line ? ". " : ""}Source: ${network}`;
  if (seasonNumber) line += `${line ? ". " : ""}Season ${seasonNumber}`;

  return withPeriod(compactWhitespace(line));
}

function findSentenceBreaks(plot) {
  const breaks = [];
  for (let i = 0; i < plot.length; i++) {
    if (/[.!?—]/.test(plot[i])) {
      breaks.push(i);
    }
  }
  return breaks;
}

function buildRawMetaDescription(show, curationReasons = [], relatedShows = []) {
  const plot = compactWhitespace(getShowField(show, "plot"));
  const factLine = buildFactLine(show);

  // Pick the most specific curation reason (rating threshold is first if present)
  const rawReason = curationReasons[0];
  const curationNote = rawReason
    ? withPeriod(rawReason.charAt(0).toUpperCase() + rawReason.slice(1))
    : "";

  const topRelatedTitle = relatedShows
    .map((related) => (typeof related?.title === "string" ? related.title.trim() : ""))
    .find((title) => title);
  const relatedNote = topRelatedTitle ? `Pairs with ${topRelatedTitle}.` : "";

  if (plot.length >= MIN_PLOT_LENGTH) {
    // Lead with factLine, then plot excerpt, then curation note if space allows
    const prefix = factLine ? `${factLine} ` : "";
    const remaining = MAX_DESCRIPTION_LENGTH - prefix.length;

    const tryAppendNotes = (text) => {
      let candidate = text;
      if (curationNote && `${candidate} ${curationNote}`.length <= MAX_DESCRIPTION_LENGTH) {
        candidate = `${candidate} ${curationNote}`;
      }
      if (relatedNote && `${candidate} ${relatedNote}`.length <= MAX_DESCRIPTION_LENGTH) {
        candidate = `${candidate} ${relatedNote}`;
      }
      return candidate;
    };

    // If full plot fits after prefix, use it
    const fullPlot = withPeriod(plot);
    if (prefix.length + fullPlot.length <= MAX_DESCRIPTION_LENGTH) {
      return compactWhitespace(tryAppendNotes(`${prefix}${fullPlot}`));
    }

    // Find longest sentence break within remaining budget
    const breaks = findSentenceBreaks(plot);
    for (let i = breaks.length - 1; i >= 0; i--) {
      const excerpt = plot.slice(0, breaks[i] + 1).trimEnd();
      if (excerpt.length >= MIN_PLOT_LENGTH && excerpt.length <= remaining) {
        return compactWhitespace(tryAppendNotes(`${prefix}${excerpt}`));
      }
    }

    // Try comma boundary (at least 70% of remaining budget)
    const minCommaPos = Math.max(MIN_PLOT_LENGTH, Math.floor(remaining * 0.7));
    for (let i = remaining - 1; i >= minCommaPos; i--) {
      if (plot[i] === ",") {
        const excerpt = plot.slice(0, i + 1).trimEnd();
        return compactWhitespace(tryAppendNotes(`${prefix}${excerpt}`));
      }
    }

    // Word-boundary truncation with ellipsis
    if (remaining - 1 >= MIN_PLOT_LENGTH) {
      const truncated = plot.slice(0, remaining - 1).replace(/\s+\S*$/, "").trimEnd();
      return compactWhitespace(`${prefix}${truncated}…`);
    }
  }

  // Fallback: fact-based description with show name (no plot available)
  const title = getShowField(show, "title");
  const year = getShowField(show, "year");
  const namedFactLine = withPeriod(compactWhitespace(
    `${title}${year ? ` (${year})` : ""}${factLine ? ` ${factLine.charAt(0).toLowerCase()}${factLine.slice(1)}` : ""}`
  ));
  const segments = [namedFactLine];
  const episodeCount = getShowField(show, "episodeCount");
  const rating = getShowField(show, "rating");
  const originalTitle = getShowField(show, "originalTitle");

  if (episodeCount) segments.push(withPeriod(`${episodeCount} episodes`));
  if (rating) segments.push(withPeriod(`IMDb: ${rating}/10`));
  if (originalTitle) segments.push(withPeriod(`Also known as ${originalTitle}`));
  if (curationNote) segments.push(curationNote);
  if (relatedNote) segments.push(relatedNote);

  let description = compactWhitespace(segments.filter(Boolean).join(" "));
  if (description.length <= MAX_DESCRIPTION_LENGTH) {
    return description;
  }

  // Drop extras until it fits
  description = compactWhitespace(factLine);
  if (description.length <= MAX_DESCRIPTION_LENGTH) {
    return description;
  }

  return `${description.slice(0, MAX_DESCRIPTION_LENGTH - 1).trimEnd()}…`;
}

export function buildMetaDescription(show, curationReasons = [], relatedShows = []) {
  return cleanViewerFacingCopy(
    buildRawMetaDescription(show, curationReasons, relatedShows),
    { protectedPhrases: [getShowField(show, "title")] }
  );
}

export function buildShowSeoMeta({ show, slug, curationReasons = [], relatedShows = [] }) {
  const title = getShowField(show, "title");
  const year = getShowField(show, "year");
  const network = getSourceLabel(show);
  const metaTitle = buildShowTitle(title, year, network);
  const description = buildMetaDescription(show, curationReasons, relatedShows);
  const canonicalUrl = `${SITE_URL}/show/${slug}`;
  const image = getPosterUrl(show) || DEFAULT_OG_IMAGE;
  const locale = getShowLocale(show);

  return {
    title: metaTitle,
    description,
    canonicalUrl,
    ogUrl: canonicalUrl,
    ogType: "video.tv_show",
    ogLocale: locale,
    image,
    robots: "index,follow",
  };
}
